import { useEffect, useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import { resolveRatePlanMatchesWithAi } from '~/components/Vendors/Profile/SetUpFlashOffer/PackageSetupStep/llmRatePlanMatcher';
import type { RatePlanMatchRequest } from '~/components/Vendors/Profile/SetUpFlashOffer/PackageSetupStep/ratePlanMatchPrompt';
import {
  getUnansweredRequests,
  readRatePlanSuggestions,
  recordRatePlanAnswers,
} from '~/components/Vendors/Profile/SetUpFlashOffer/PackageSetupStep/ratePlanSuggestionCache';
import type { RatePlanAnswers } from '~/components/Vendors/Profile/SetUpFlashOffer/PackageSetupStep/ratePlanSuggestionCache';

export const packageRatePlanSuggestionQueryKeys = {
  all: ['flash-wizard', 'package-rate-plan-suggestions'] as const,
  list: (requests: Array<RatePlanMatchRequest>) => [...packageRatePlanSuggestionQueryKeys.all, requests] as const,
};

// The AI's confident package -> rate plan name matches for packages whose room has several rate plans,
// kept per package so a change to one package only asks the AI about that package.
export function usePackageRatePlanSuggestions(requests: Array<RatePlanMatchRequest>, { enabled = true } = {}) {
  const [answers, setAnswers] = useState<RatePlanAnswers>({});
  const unansweredRequests = getUnansweredRequests(requests, answers);
  const { data: suggestionResult, isFetching } = useQuery({
    queryKey: packageRatePlanSuggestionQueryKeys.list(unansweredRequests),
    queryFn: () => resolveRatePlanMatchesWithAi(unansweredRequests),
    enabled: enabled && unansweredRequests.length > 0,
    staleTime: Infinity,
    retry: false,
  });

  useEffect(() => {
    if (suggestionResult && !suggestionResult.hasFailed) {
      setAnswers((previous) => recordRatePlanAnswers(previous, unansweredRequests, suggestionResult.matches));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- records the answers once, when the result arrives
  }, [suggestionResult]);

  return {
    hasSuggestionFailed: suggestionResult?.hasFailed ?? false,
    isSuggesting: isFetching,
    suggestedRatePlanNameByPackageId: readRatePlanSuggestions(requests, answers),
  };
}
